import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ComponentType } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { CaretRight, MagnifyingGlass, PushPin, X } from '@phosphor-icons/react';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { matchesToolQuery } from '../../utils/backoffice/backoffice-tool-search';

export type BackofficePickerItem = {
  /** Locale-less tool path, the stable key for pins and recents. */
  path: string;
  href: string;
  label: string;
  description: string;
  categoryId: string;
  categoryLabel: string;
  icon: ComponentType<IconProps>;
  isActive: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Every tool, ordered by category. */
  items: BackofficePickerItem[];
  pinnedPaths: string[];
  recentPaths: string[];
  onTogglePin: (path: string) => void;
};

type Section = { id: string; label: string; entries: { item: BackofficePickerItem; index: number }[] };

/**
 * Searchable list of every backoffice tool, opened from the top nav (button or Cmd/Ctrl+K).
 * A centered panel; uses the combobox pattern: focus stays in the search box and the arrow keys
 * move a highlighted option (aria-activedescendant).
 *
 * With an empty search box, pinned tools and the last few visited ones sit above the category
 * groups. Pin with the pin button, or Shift+Enter on the highlighted row.
 *
 * Portaled to <body> because the nav's `backdrop-filter` would otherwise become the containing
 * block for `position: fixed` and clip the overlay to the 44px bar.
 */
export function BackofficeToolPicker({ open, onClose, items, pinnedPaths, recentPaths, onTogglePin }: Props) {
  const formatMessage = useFormatMessage();
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const jumpToToolLabel = formatMessage({
    id: 'backoffice.nav.jumpToTool',
    defaultMessage: 'Jump to backoffice tool',
    description: 'Accessible label for the backoffice tool picker dialog and its list of tools',
  });
  const pinnedLabel = formatMessage({ id: 'backoffice.nav.pinned', defaultMessage: 'Pinned' });
  const recentLabel = formatMessage({ id: 'backoffice.nav.recent', defaultMessage: 'Recent' });
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const sections: Section[] = useMemo(() => {
    const byPath = new Map(items.map((item) => [item.path, item]));
    const matching = items.filter((item) =>
      matchesToolQuery(`${item.label} ${item.description} ${item.categoryLabel}`, query),
    );
    const built: { id: string; label: string; items: BackofficePickerItem[] }[] = [];
    if (query.trim() === '') {
      const pinned = pinnedPaths.map((p) => byPath.get(p)).filter((i): i is BackofficePickerItem => Boolean(i));
      // "Recent" means elsewhere: skip the page you are on and anything already pinned.
      const recent = recentPaths
        .filter((p) => !pinnedPaths.includes(p))
        .map((p) => byPath.get(p))
        .filter((i): i is BackofficePickerItem => Boolean(i) && !i!.isActive);
      if (pinned.length > 0) built.push({ id: 'pinned', label: pinnedLabel, items: pinned });
      if (recent.length > 0) built.push({ id: 'recent', label: recentLabel, items: recent });
    }
    for (const item of matching) {
      let group = built.find((g) => g.id === item.categoryId);
      if (!group) {
        group = { id: item.categoryId, label: item.categoryLabel, items: [] };
        built.push(group);
      }
      group.items.push(item);
    }
    let index = 0;
    return built.map((group) => ({
      id: group.id,
      label: group.label,
      entries: group.items.map((item) => ({ item, index: index++ })),
    }));
  }, [items, pinnedPaths, recentPaths, query, pinnedLabel, recentLabel]);

  const flat = useMemo(() => sections.flatMap((section) => section.entries.map((entry) => entry.item)), [sections]);

  // Reset on every open and start the highlight on the current tool, so Enter/arrows are
  // relative to where you are.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const currentIndex = flat.findIndex((item) => item.isActive);
    setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
    // Only the open transition should reset state; `flat` changes on every pin toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the search box, lock page scroll, and hand focus back to whatever opened us.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(`${uid}-opt-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, uid]);

  // A pin toggle can shrink the list under the highlight.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (flat.length > 0) setActiveIndex((i) => (i + 1) % flat.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (flat.length > 0) setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
      } else if (event.key === 'Home' && event.target === inputRef.current && query === '') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End' && event.target === inputRef.current && query === '') {
        event.preventDefault();
        setActiveIndex(Math.max(0, flat.length - 1));
      } else if (event.key === 'Enter' && event.target === inputRef.current) {
        const target = flat[activeIndex];
        if (!target) return;
        event.preventDefault();
        if (event.shiftKey) onTogglePin(target.path);
        else window.location.href = target.href;
      } else if (event.key === 'Tab') {
        // Two tab stops (search box, close button): keep focus inside the modal.
        const first = inputRef.current;
        const last = closeRef.current;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    },
    [activeIndex, flat, onClose, onTogglePin, query],
  );

  if (!open || typeof document === 'undefined') return null;

  const activeOptionId = flat[activeIndex] ? `${uid}-opt-${activeIndex}` : undefined;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/35 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={jumpToToolLabel}
        onKeyDown={onKeyDown}
        className="absolute left-1/2 top-[8vh] flex max-h-[78vh] w-[min(44rem,calc(100vw-2rem))] -translate-x-1/2 flex-col rounded-2xl border border-jc-border bg-jc-surface text-jc-ink shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-jc-border px-4 py-2">
          <MagnifyingGlass className="h-5 w-5 shrink-0 text-jc-muted" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            aria-autocomplete="list"
            aria-label={formatMessage({ id: 'backoffice.nav.searchLabel', defaultMessage: 'Search backoffice tools' })}
            placeholder={formatMessage({ id: 'backoffice.nav.searchPlaceholder', defaultMessage: 'Search tools…' })}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className="min-h-11 min-w-0 flex-1 bg-transparent text-sm text-jc-ink placeholder:text-jc-muted focus:outline-none"
          />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={formatMessage({ id: 'backoffice.nav.close', defaultMessage: 'Close' })}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-jc-muted transition-colors hover:bg-black/5 hover:text-jc-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent/50 dark:hover:bg-white/10"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div
          id={listboxId}
          role="listbox"
          aria-label={jumpToToolLabel}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2 pt-1"
        >
          {flat.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-jc-muted" role="status">
              {formatMessage(
                { id: 'backoffice.nav.noResults', defaultMessage: 'No tools match "{query}".' },
                { query: query.trim() },
              )}
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.id} role="group" aria-labelledby={`${uid}-group-${section.id}`} className="mt-2 first:mt-1">
                <div
                  id={`${uid}-group-${section.id}`}
                  className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-jc-muted"
                >
                  {section.label}
                </div>
                {section.entries.map(({ item, index }) => {
                  const Icon = item.icon;
                  const isHighlighted = index === activeIndex;
                  const isPinned = pinnedPaths.includes(item.path);
                  const pinLabel = isPinned
                    ? formatMessage({ id: 'backoffice.nav.unpin', defaultMessage: 'Unpin {tool}' }, { tool: item.label })
                    : formatMessage({ id: 'backoffice.nav.pin', defaultMessage: 'Pin {tool}' }, { tool: item.label });
                  return (
                    <div
                      key={`${section.id}:${item.path}`}
                      role="presentation"
                      className={`group flex items-center rounded-xl transition-colors ${isHighlighted ? 'bg-jc-accent-muted' : ''}`}
                    >
                      <a
                        id={`${uid}-opt-${index}`}
                        role="option"
                        aria-selected={isHighlighted}
                        aria-current={item.isActive ? 'page' : undefined}
                        href={item.href}
                        tabIndex={-1}
                        onMouseMove={() => {
                          if (!isHighlighted) setActiveIndex(index);
                        }}
                        className="flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-xl px-3 py-2"
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            item.isActive ? 'bg-jc-cta text-white' : 'bg-jc-elevated text-jc-ink-secondary'
                          }`}
                          aria-hidden
                        >
                          <Icon className="h-[18px] w-[18px]" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-jc-ink">{item.label}</span>
                            {item.isActive ? (
                              <span className="shrink-0 rounded-full bg-jc-accent-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-jc-accent">
                                {formatMessage({ id: 'backoffice.nav.current', defaultMessage: 'Current' })}
                              </span>
                            ) : null}
                          </span>
                          <span className="line-clamp-2 text-xs text-jc-muted">{item.description}</span>
                        </span>
                        <CaretRight className="h-4 w-4 shrink-0 text-jc-muted" aria-hidden />
                      </a>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={pinLabel}
                        aria-pressed={isPinned}
                        title={pinLabel}
                        onClick={() => onTogglePin(item.path)}
                        className={`mr-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${
                          isPinned ? 'text-jc-accent' : 'text-jc-muted opacity-60 group-hover:opacity-100'
                        }`}
                      >
                        <PushPin className="h-4 w-4" weight={isPinned ? 'fill' : 'regular'} aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="shrink-0 border-t border-jc-border px-4 py-2 text-[11px] text-jc-muted">
          {formatMessage({
            id: 'backoffice.nav.hint',
            defaultMessage: 'Arrow keys to move, Enter to open, Shift+Enter to pin or unpin',
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
